import {Component, Inject, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {IdName} from '@domain/id-name';
import {FixedSchedule} from '@domain/planning-prototype/carpet/fixed-schedule';
import {FixedSchedulePlaceholder} from '@domain/planning-prototype/carpet/fixed-schedule-placeholder';
import {GenericOrder} from '@domain/planning-prototype/generic-order.interface';
import {GenericRun} from '@domain/planning-prototype/generic-run.interface';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {PlanningItemFactory} from '@domain/planning-prototype/planning-item-factory';
import {PlanningItemType} from '@domain/planning-prototype/planning-item-type';
import {OrderPlastic} from '@domain/planning-prototype/plastic/order-plastic';
import {ProductionOrderToPlan} from '@domain/planning-prototype/to-plan/production-order-to-plan';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {ListOverviewActionsComponent} from '@presentation/pages/texfab/production-schedule/overview/actions/list-overview-actions.component';
import {ListOverviewAction} from '@presentation/pages/texfab/production-schedule/overview/list-overview-action';
import {ProductionScheduleOverviewStatusComponent} from '@presentation/pages/texfab/production-schedule/overview/status/production-schedule-overview-status.component';
import {AssertionUtils, BaseComponent, ColDefBuilderFactoryService, DialogBuilderFactoryService, GridOptionsBuilderFactoryService, TranslateService} from '@vdw/angular-component-library';
import {
  CellClickedEvent,
  ColDef,
  GetRowIdParams,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
  RowDragCallbackParams,
  RowDragEndEvent,
  ValueGetterParams
} from 'ag-grid-community';
import {filter, takeUntil} from 'rxjs';
import {PlanningPrototypeContextService} from '../../../utilities/planning-prototype-context.service';
import {PlanningRunSidebarComponent} from '../planning-run-sidebar.component';
import {SelectFixedScheduleParams} from '../select-fixed-schedule/select-fixed-schedule-params';
import {SelectFixedScheduleComponent} from '../select-fixed-schedule/select-fixed-schedule.component';
import {SelectUnplannedOrderParameters} from '../select-unplanned-order/select-unplanned-order-parameters';
import {SelectUnplannedOrderComponent} from '../select-unplanned-order/select-unplanned-order.component';
import {NavigationProductionOrderList} from './navigation-production-order-list.interface';

@Component({
  selector: 'app-production-orders-list',
  templateUrl: './production-orders-list.component.html',
  styleUrls: ['./production-orders-list.component.scss']
})
export class ProductionOrdersListComponent extends BaseComponent implements OnInit {
  private currentSubscription: Subscription;
  public gridOptions: GridOptions;

  public get planningItemsForm(): FormControl<PlanningItem[]> {
    return this.parent.runForm.controls.planningItems;
  }

  public get planningItems(): PlanningItem[] {
    return this.planningItemsForm.value;
  }

  public get showFixedSchedules(): boolean {
    return (
      this.parent.run.type === PlanningItemType.RUN_CARPET &&
      !AssertionUtils.isEmpty(this.planningContext.fixedSchedulesToPlan) &&
      this.currentSubscription?.hasPermission(Permission.FIXED_SCHEDULE_EDIT)
    );
  }

  public constructor(
    private readonly planningContext: PlanningPrototypeContextService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly parent: PlanningRunSidebarComponent,
    private readonly dialogBuilder: DialogBuilderFactoryService,
    private readonly navigationService: NavigationHelperService<NavigationProductionOrderList>,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.planningContext.selectedItemChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy), filter(PlanningItemHelper.isRunItem)).subscribe((run: GenericRun) => {
      const showActionsColumn = AssertionUtils.isNullOrUndefined(run.actualEndDate);
      this.setGridOptions(showActionsColumn);
    });
  }

  public onNavigationHelperDestroy(): void {
    this.navigationService.savePartialState<NavigationProductionOrderList>({
      gridOptions: this.gridOptions
    });
  }

  public addProductionOrders(): void {
    this.dialogBuilder
      .getBuilder()
      .openSelectGridDialog(SelectUnplannedOrderComponent, {
        type: PlanningItemHelper.childTypeForRunType(this.parent.run.type),
        parentItemId: this.parent.run.draftId,
        itemsSelectedAlready: this.planningItems,
        creel: this.parent.runForm.controls.creel.value,
        machineQuality: this.parent.runForm.controls.machineQuality.value,
        product: this.parent.runForm.controls.weaveProduct.value ?? this.parent.runForm.controls.tuftProduct.value ?? this.parent.runForm.controls.tool.value
      } as SelectUnplannedOrderParameters)
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((result: any) => !AssertionUtils.isNullOrUndefined(result))
      )
      .subscribe((result: ProductionOrderToPlan[]) => {
        let maxSequence = this.planningItems
          .map((item: PlanningItem) => item.sequenceNumber)
          .reduce((sequenceNumber: number, currentMax: number) => (sequenceNumber > currentMax ? sequenceNumber : currentMax), 0);
        this.planningItemsForm.setValue([
          ...this.planningItems,
          ...result.map((orderToPlan: ProductionOrderToPlan) => PlanningItemFactory.createOrderForToPlan(orderToPlan, this.parent.machine, ++maxSequence))
        ]);
        this.planningItemsForm.markAsDirty();
      });
  }

  public addFixedSchedules(): void {
    const creel = this.parent.runForm.controls.creel.value;
    const machineQuality = this.parent.runForm.controls.machineQuality.value;
    const machine = this.parent.machine;
    this.dialogBuilder
      .getBuilder()
      .openSelectGridDialog(SelectFixedScheduleComponent, {
        parentItemId: this.parent.run.draftId,
        itemsSelectedAlready: this.planningItems,
        creel: new IdName(creel.id, creel.name),
        machineQuality: machineQuality as IdName,
        machine
      } as SelectFixedScheduleParams)
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((result: any) => !AssertionUtils.isNullOrUndefined(result))
      )
      .subscribe((result: FixedSchedule[]) => {
        let maxSequence = this.planningItems
          .map((item: PlanningItem) => item.sequenceNumber)
          .reduce((sequenceNumber: number, currentMax: number) => (sequenceNumber > currentMax ? sequenceNumber : currentMax), 0);
        this.planningItemsForm.setValue([...this.planningItems, ...result.map((fixedSchedule: FixedSchedule) => PlanningItemFactory.createPlaceholderForFixedSchedule(fixedSchedule, ++maxSequence))]);
        this.planningItemsForm.markAsDirty();
      });
  }

  public onRowDragEnd = (event: RowDragEndEvent<PlanningItem>): void => {
    const rows: PlanningItem[] = [];
    event.api.forEachNode((node: IRowNode<PlanningItem>) => rows.push(node.data));

    this.planningItems.sort((a: PlanningItem, b: PlanningItem) => rows.indexOf(a) - rows.indexOf(b));
    this.planningItemsForm.markAsDirty();
  };

  private setGridOptions(showActionsColumn: boolean): void {
    let obj = {gridOptions: null};
    let prevState = this.navigationService.getPartialState<NavigationProductionOrderList>(Object.keys(obj));
    if (!AssertionUtils.isNullOrUndefined(prevState)) {
      this.gridOptions = prevState.gridOptions;
    } else {
      this.gridOptions = this.gridOptionsBuilderFactoryService
        .getBuilder(this.getColumnDefs(showActionsColumn), GridIdentifier.PRODUCTION_ORDERS_LIST)
        .withSuppressRowClickSelection()
        .withSuppressRowHoverHighlight()
        .withLockPinnedColumns()
        .withOnGridReady((params: GridReadyEvent) => params.api.sizeColumnsToFit())
        .withGetRowId((params: GetRowIdParams<GenericOrder | FixedSchedulePlaceholder>) => params.data.draftId)
        .withRowDragManaged()
        .withOnRowDragEnd(this.onRowDragEnd)
        .withSuppressMoveWhenRowDragging()
        .withPopupParent(document.querySelector('body'))
        .build();
    }
  }

  private deleteOrderFromRun(item: PlanningItem): void {
    this.planningItemsForm.setValue(this.planningItems.filter((itemInList: PlanningItem) => itemInList !== item));
    this.planningItemsForm.markAsDirty();
  }

  private getColumnDefs(showActionsColumn: boolean): ColDef[] {
    let colDefs = [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withRowDrag((params: RowDragCallbackParams<PlanningItem>) => AssertionUtils.isNullOrUndefined(params.data.actualStartDate))
        .withHeaderName('GENERAL.NAME')
        .withSuppressMovable()
        .withLockVisible()
        .withSortable(false)
        .withoutFilter()
        .withCellClass('url')
        .withOnCellClicked((params: CellClickedEvent<PlanningItem>): void => {
          if (!this.planningItemsForm.dirty) {
            this.planningContext.selectItem(params.data);
          }
        })
        .withValueGetter((params: ValueGetterParams<PlanningItem>) => PlanningItemHelper.getItemName(params.data))
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withField('productionOrder.dueDate').withHeaderName('GENERAL.DATETIME.DUE_DATE').withDate().withSortable(false).withoutFilter().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('productionOrder.status')
        .withHeaderName('GENERAL.STATUS')
        .withCellClass('center')
        .withTooltipValueGetter((params: ITooltipParams): any => this.translate.instant(`PRODUCTION_ORDER.STATUS.${params.value}`))
        .withCellRenderer(ProductionScheduleOverviewStatusComponent, (params: ICellRendererParams) =>
          params.data instanceof OrderPlastic ? {withNewIcons: true, state: params.data.productionOrder.state} : {}
        )
        .withValueGetter((params: ValueGetterParams<PlanningItem>) => PlanningItemHelper.getItemStatus(params.data))
        .withSortable(false)
        .withoutFilter()
        .build()
    ];

    if (showActionsColumn) {
      colDefs.push(
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('GENERAL.ACTIONS.ACTIONS')
          .withHeaderClass('actions-header')
          .withCellRenderer(ListOverviewActionsComponent)
          .withValueGetter((params: ValueGetterParams<PlanningItem>): ListOverviewAction[] => {
            if (!AssertionUtils.isNullOrUndefined(params.data.actualStartDate)) {
              return [];
            }
            return [
              {
                name: 'GENERAL.ACTIONS.DELETE',
                actionFn: () => this.deleteOrderFromRun(params.data)
              }
            ];
          })
          .withSuppressMovable('right')
          .withLockVisible()
          .withSortable(false)
          .withoutFilter()
          .build()
      );
    }

    return colDefs;
  }
}
